import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DIAPER_KINDS,
  ENTRY_META,
  ENTRY_TYPES,
  MEAL_AMOUNTS,
  MEAL_OPTIONS,
  NAP_QUALITY,
  POTTY_RESULTS,
  nowTimeStr,
} from "@/lib/entryTypes";

/**
 * Mobile-friendly sheet for adding a single report entry. Renders a
 * type-specific micro-form driven by chips/toggles to minimise typing.
 */
export default function AddEntrySheet({
  entryType,
  open,
  onOpenChange,
  onSubmit,
  restoreFocusRef,
}) {
  const [detail, setDetail] = useState({});

  useEffect(() => {
    if (!open) return;
    // Sensible defaults per type.
    switch (entryType) {
      case ENTRY_TYPES.MEAL:
        setDetail({ meal: "lunch", amount: "most", items: "" });
        break;
      case ENTRY_TYPES.NAP: {
        const start = nowTimeStr();
        setDetail({ start_time: start, end_time: "", quality: "sound" });
        break;
      }
      case ENTRY_TYPES.DIAPER:
        setDetail({ kind: "wet", notes: "" });
        break;
      case ENTRY_TYPES.POTTY:
        setDetail({ result: "success" });
        break;
      case ENTRY_TYPES.ACTIVITY:
        setDetail({ title: "", description: "" });
        break;
      case ENTRY_TYPES.NOTE:
        setDetail({ body: "" });
        break;
      default:
        setDetail({});
    }
  }, [entryType, open]);

  if (!entryType) return null;
  const meta = ENTRY_META[entryType];

  const submit = (e) => {
    e?.preventDefault?.();
    onSubmit(detail);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`add-entry-sheet-${entryType}`}
        className="max-w-md rounded-2xl border-neutral-200 bg-white p-0"
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          restoreFocusRef?.current?.focus?.({ preventScroll: true });
        }}
      >
        <DialogHeader className="border-b border-neutral-100 px-6 pb-4 pt-5">
          <DialogTitle className="font-heading text-xl font-semibold tracking-tight text-neutral-900">
            Add {meta.label.toLowerCase()}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 px-6 py-5">
          {entryType === ENTRY_TYPES.MEAL && (
            <>
              <ChipRow
                testId="meal-type"
                label="Meal"
                value={detail.meal}
                options={MEAL_OPTIONS}
                onChange={(v) => setDetail((d) => ({ ...d, meal: v }))}
              />
              <ChipRow
                testId="meal-amount"
                label="Amount eaten"
                value={detail.amount}
                options={MEAL_AMOUNTS}
                onChange={(v) => setDetail((d) => ({ ...d, amount: v }))}
              />
              <TextField
                testId="meal-items"
                label="Items (optional)"
                value={detail.items}
                placeholder="e.g. pasta, peas, strawberries"
                onChange={(v) => setDetail((d) => ({ ...d, items: v }))}
              />
            </>
          )}

          {entryType === ENTRY_TYPES.NAP && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <TimeField
                  testId="nap-start"
                  label="Start"
                  value={detail.start_time}
                  onChange={(v) => setDetail((d) => ({ ...d, start_time: v }))}
                />
                <TimeField
                  testId="nap-end"
                  label="End"
                  value={detail.end_time}
                  onChange={(v) => setDetail((d) => ({ ...d, end_time: v }))}
                />
              </div>
              <ChipRow
                testId="nap-quality"
                label="Quality"
                value={detail.quality}
                options={NAP_QUALITY}
                onChange={(v) => setDetail((d) => ({ ...d, quality: v }))}
              />
            </>
          )}

          {entryType === ENTRY_TYPES.DIAPER && (
            <>
              <ChipRow
                testId="diaper-kind"
                label="Type"
                value={detail.kind}
                options={DIAPER_KINDS}
                labelMap={{ bm: "BM" }}
                onChange={(v) => setDetail((d) => ({ ...d, kind: v }))}
              />
              <TextField
                testId="diaper-notes"
                label="Notes (optional)"
                value={detail.notes}
                onChange={(v) => setDetail((d) => ({ ...d, notes: v }))}
              />
            </>
          )}

          {entryType === ENTRY_TYPES.POTTY && (
            <ChipRow
              testId="potty-result"
              label="Result"
              value={detail.result}
              options={POTTY_RESULTS}
              onChange={(v) => setDetail((d) => ({ ...d, result: v }))}
            />
          )}

          {entryType === ENTRY_TYPES.ACTIVITY && (
            <>
              <TextField
                testId="activity-title"
                label="Title"
                value={detail.title}
                placeholder="e.g. Sensory table"
                onChange={(v) => setDetail((d) => ({ ...d, title: v }))}
              />
              <TextArea
                testId="activity-description"
                label="Description (optional)"
                value={detail.description}
                onChange={(v) => setDetail((d) => ({ ...d, description: v }))}
              />
            </>
          )}

          {entryType === ENTRY_TYPES.NOTE && (
            <TextArea
              testId="note-body"
              label="Note"
              value={detail.body}
              placeholder="Anything the family should know…"
              onChange={(v) => setDetail((d) => ({ ...d, body: v }))}
            />
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              data-testid="add-entry-cancel"
              onClick={() => onOpenChange(false)}
              className="rounded-full px-5 py-2.5 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="add-entry-submit"
              className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-primary-hover active:scale-[0.98]"
            >
              Add
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------- tiny input primitives --------------------------

function ChipRow({ label, value, options, onChange, testId, labelMap = {} }) {
  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </p>
      <div className="flex flex-wrap gap-2" data-testid={testId}>
        {options.map((opt) => {
          const selected = value === opt;
          return (
            <button
              key={opt}
              type="button"
              data-testid={`${testId}-${opt}`}
              onClick={() => onChange(opt)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-all active:scale-[0.97] ${
                selected
                  ? "border-primary bg-primary/10 text-primary-hover"
                  : "border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50"
              }`}
            >
              {labelMap[opt] ||
                opt.charAt(0).toUpperCase() + opt.slice(1).replace(/_/g, " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <input
        data-testid={testId}
        type="text"
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <textarea
        data-testid={testId}
        rows={3}
        value={value || ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}

function TimeField({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
        {label}
      </span>
      <input
        data-testid={testId}
        type="time"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-900 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
    </label>
  );
}
